import { useSelector } from 'react-redux';
import { useState } from 'react';
import { selectStripeLinks } from './selector';
import styles from './styles.scss';
import { isMobileLayout as isMobileLayoutSelector } from 'components/Shared/responsive';
import { IconCheckmark } from 'components/Shared/Icon';
import { StripeLink } from 'models/StripeLink';
import { hasElements } from 'utils';
import cn from 'classnames';
import { useInterval } from 'hooks';
import { StarRating } from 'components/Shared/StarRating';
import { useExperiment } from 'experiments/experiment';
import experiment from 'experiments/stripeLink.experiment';
import { PageState } from 'models/PageState';
interface StripeRollingLinksProps {
  stripeLinks: StripeLink[];
  saleShop: string;
}

interface StripeLinkComponentProps {
  item: StripeLink;
  saleShop: string;
}

const isInternalLink = (url: string, saleShop: string): boolean => {
  try {
    const linkUrl = new URL(url);
    return linkUrl.hostname.includes(saleShop.toLowerCase());
  } catch {
    return false;
  }
};

export const StripeLinkComponent = ({ item, saleShop }: StripeLinkComponentProps) => {
  const isInternal = isInternalLink(item.url, saleShop);

  return (
    <a
      href={item.url}
      target={isInternal ? '_self' : '_blank'}
      rel={isInternal ? undefined : 'noopener noreferrer'}
    >
      {item.title}
    </a>
  );
};

export const Stripe = () => {
  const { variant } = useExperiment({ experiment });
  const saleShop = useSelector((state: PageState) => state.appShell.saleShop);
  const stripeLinks = useSelector(selectStripeLinks);
  const isMobileLayout = useSelector(isMobileLayoutSelector);

  if (variant > 0 || !hasElements(stripeLinks)) {
    return null;
  }

  if (isMobileLayout) {
    return <StripeRollingLinks stripeLinks={stripeLinks} saleShop={saleShop} />;
  }

  return (
    <div className={cn(styles.stripe)}>
      {stripeLinks.map((item, index) => (
        <div className={cn(styles.stripeItem)} key={index}>
          <IconCheckmark size="small" className={styles.uspIcon} />
          {item.rating && (
            <div className={styles.starRating}>
              <StarRating score={item.rating / 10} color="#777777" emptyStarColor="#c7c3c3" />
            </div>
          )}
          {item.url ? (
            <StripeLinkComponent item={item} saleShop={saleShop} />
          ) : (
            <span>{item.title}</span>
          )}
        </div>
      ))}
    </div>
  );
};

const StripeRollingLinks = ({ stripeLinks, saleShop }: StripeRollingLinksProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState<number | null>(null);

  useInterval(() => {
    if (stripeLinks.length <= 1) return;

    setCurrentIndex(prev => {
      setPrevIndex(prev);
      return (prev + 1) % stripeLinks.length;
    });
  }, 5000);

  return (
    <div className={cn(styles.stripe)}>
      {prevIndex !== null && (
        <div
          key={`prev-${prevIndex}`}
          className={cn(styles.stripeItem, styles.exit)}
          onAnimationEnd={() => setPrevIndex(null)}
        >
          <IconCheckmark size="small" className={styles.uspIcon} />
          {stripeLinks[prevIndex].rating && (
            <div className={styles.starRating}>
              <StarRating
                score={(stripeLinks[prevIndex]?.rating ?? 0) / 10}
                emptyStarColor="#c7c3c3"
                color="#777777"
              />
            </div>
          )}
          {stripeLinks[prevIndex].url ? (
            <StripeLinkComponent item={stripeLinks[prevIndex]} saleShop={saleShop} />
          ) : (
            <span>{stripeLinks[prevIndex].title}</span>
          )}
        </div>
      )}
      <div key={`cur-${currentIndex}`} className={cn(styles.stripeItem, styles.enter)}>
        <IconCheckmark size="small" className={styles.uspIcon} />
        {stripeLinks[currentIndex].rating && (
          <div className={styles.starRating}>
            <StarRating
              score={(stripeLinks[currentIndex]?.rating ?? 0) / 10}
              emptyStarColor="#c7c3c3"
              color="#777777"
            />
          </div>
        )}
        {stripeLinks[currentIndex].url ? (
          <StripeLinkComponent item={stripeLinks[currentIndex]} saleShop={saleShop} />
        ) : (
          <span>{stripeLinks[currentIndex].title}</span>
        )}
      </div>
    </div>
  );
};
